import { useInfiniteQuery } from '@tanstack/react-query'
import {
  Alert,
  Button,
  Card,
  Col,
  Empty,
  Flex,
  Input,
  Row,
  Segmented,
  Select,
  Skeleton,
  Spin,
  Tag,
  Typography
} from 'antd'
import { ExternalLink, Plus, Search } from 'lucide-react'
import { useEffect, useMemo, useState, type MouseEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useNavigate } from 'react-router-dom'

import type { WorkspaceApp } from 'src/api/facade/types'
import {
  applicationsApplicationsRepositoriesListInfiniteOptions,
  type ApplicationRepository
} from 'src/api/main'
import { DateTimeCell, SectionLabel } from 'src/components'
import {
  useDebouncedValue,
  useHasWorkspacePermissions,
  useListQueryState
} from 'src/hooks'
import { paths } from 'src/router'
import { WorkspacePermissionCode } from 'src/utils'

import { useWorkspaceAppAccessCounts } from '../../hooks/useWorkspaceAppAccessCounts'
import { useWorkspaceAppsList } from '../../hooks/useWorkspaceAppsList'
import { useWorkspaceDomainName } from '../../hooks/useWorkspaceDomainName'
import { buildAppLaunchUrl } from '../../utils/appLaunchUrl'
import {
  BOARD_ORDERING_VALUES,
  BOARD_STATE_VALUES,
  filterAvailable,
  filterInstalled,
  normalizeNeedle,
  sortAvailable,
  sortInstalled,
  type BoardOrdering,
  type BoardState
} from '../../utils/appsBoard'
import { AppIcon } from '../AppIcon'
import { AppTypeIcon } from '../AppTypeIcon'

import { InstallRepoDrawer } from './InstallRepoDrawer'

const { Text, Paragraph } = Typography

type Props = {
  workspaceId: number
  onCreateCustom: () => void
}

const BOARD_SCHEMA = {
  search: { type: 'string', default: '' },
  state: { type: 'enum', values: BOARD_STATE_VALUES, default: 'all' },
  ordering: { type: 'enum', values: BOARD_ORDERING_VALUES, default: 'name' }
} as const

const PER_PAGE = 24

// A modified click is the browser's to interpret (new tab, new window), and a
// plain click on the title <Link> already navigated — either way the card's
// own handler must stay out of it.
const isModifiedClick = (e: MouseEvent<HTMLElement>) =>
  e.defaultPrevented || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey

/**
 * Single board for everything the workspace can run. `is_installed` on the
 * repositories endpoint already carries the installed/available split, so the
 * two former tabs collapse into one view with state as a filter.
 */
export const AppsBoard = ({ workspaceId, onCreateCustom }: Props) => {
  const canManageApps = useHasWorkspacePermissions(
    WorkspacePermissionCode.APPLICATIONS_MANAGE
  )
  const { t } = useTranslation()
  const navigate = useNavigate()

  const list = useListQueryState(BOARD_SCHEMA)
  const { search, state, ordering } = list.values

  const [installRepo, setInstallRepo] = useState<ApplicationRepository | null>(
    null
  )

  // Local mirror so the search input feels instant; debounced URL commit.
  const [searchDraft, setSearchDraft] = useState(search)
  const [searchSeen, setSearchSeen] = useState(search)
  if (search !== searchSeen) {
    setSearchSeen(search)
    setSearchDraft(search)
  }
  const debouncedSearch = useDebouncedValue(searchDraft, 300)
  useEffect(() => {
    if (debouncedSearch === searchDraft && debouncedSearch !== search) {
      list.set('search', debouncedSearch)
    }
  }, [debouncedSearch, searchDraft, search, list])

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading: isLoadingRepos,
    isError,
    refetch
  } = useInfiniteQuery({
    ...applicationsApplicationsRepositoriesListInfiniteOptions({
      // The schema types this filter as a string now.
      query: {
        organization: String(workspaceId),
        per_page: PER_PAGE,
        ordering: 'name'
      }
    }),
    getNextPageParam: ({ next }) => next ?? undefined,
    enabled: Number.isFinite(workspaceId) && workspaceId > 0
  })

  const { apps, isLoading: isLoadingApps } = useWorkspaceAppsList(workspaceId)
  const { countsByAppId, isCountReliable } =
    useWorkspaceAppAccessCounts(workspaceId)
  // Same cached org-retrieve query the page shell uses — no extra request.
  const workspaceDomain = useWorkspaceDomainName(workspaceId)

  const allRepos = useMemo(
    () => data?.pages?.flatMap((p) => p.results ?? []) ?? [],
    [data?.pages]
  )

  // A workspace runs a handful of apps, so pull every page rather than making
  // the admin scroll to discover what is installable.
  useEffect(() => {
    if (!hasNextPage || isFetchingNextPage) {
      return
    }
    void fetchNextPage()
  }, [hasNextPage, isFetchingNextPage, fetchNextPage])

  const needle = normalizeNeedle(debouncedSearch)

  const installed = useMemo(
    () => sortInstalled(filterInstalled(apps, needle), ordering),
    [apps, needle, ordering]
  )
  const available = useMemo(
    () => sortAvailable(filterAvailable(allRepos, needle), ordering),
    [allRepos, needle, ordering]
  )

  const showInstalled = state !== 'available'
  const showAvailable = state !== 'installed'
  const isLoading = isLoadingApps || (isLoadingRepos && allRepos.length === 0)
  const isEmpty =
    !isLoading &&
    (!showInstalled || installed.length === 0) &&
    (!showAvailable || available.length === 0)

  const detailPath = (app: WorkspaceApp) =>
    paths.workspaces.apps.details(String(workspaceId), app.id)

  return (
    <Flex vertical gap={14}>
      <Card size="small" variant="outlined" styles={{ body: { padding: 12 } }}>
        <Flex align="center" gap={8} wrap>
          <Input
            size="small"
            prefix={<Search size={14} color="var(--ant-color-text-tertiary)" />}
            placeholder={t('workspace.apps.board.searchPlaceholder')}
            variant="filled"
            allowClear
            value={searchDraft}
            onChange={(e) => setSearchDraft(e.target.value)}
            style={{ width: 240 }}
          />
          <Segmented<BoardState>
            size="small"
            value={state}
            onChange={(v) => list.set('state', v)}
            options={[
              {
                value: 'all',
                label: `${t('workspace.apps.board.filterAll')} · ${installed.length + available.length}`
              },
              {
                value: 'installed',
                label: `${t('workspace.apps.board.filterInstalled')} · ${installed.length}`
              },
              {
                value: 'available',
                label: `${t('workspace.apps.board.filterAvailable')} · ${available.length}`
              }
            ]}
          />
          <Select<BoardOrdering>
            size="small"
            value={ordering}
            onChange={(v) => list.set('ordering', v)}
            style={{ width: 150 }}
            aria-label={t('workspace.apps.board.sortLabel')}
            options={BOARD_ORDERING_VALUES.map((value) => ({
              value,
              label: t(`workspace.apps.board.sort.${value}`)
            }))}
          />
          <div style={{ flex: 1 }} />
          {canManageApps ? (
            <Button
              size="small"
              icon={<Plus size={14} />}
              onClick={onCreateCustom}
            >
              {t('workspace.apps.board.createCustom')}
            </Button>
          ) : null}
        </Flex>
      </Card>

      {isError ? (
        <Alert
          type="error"
          showIcon
          title={t('workspace.apps.loadError')}
          action={
            <Button size="small" onClick={() => void refetch()}>
              {t('workspace.apps.retry')}
            </Button>
          }
        />
      ) : null}

      {isLoading ? (
        <Card size="small" variant="outlined">
          <Skeleton active paragraph={{ rows: 5 }} />
        </Card>
      ) : null}

      {isEmpty ? (
        <Card
          size="small"
          variant="outlined"
          styles={{ body: { padding: 40 } }}
        >
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={
              needle
                ? t('workspace.apps.board.empty.filtered')
                : t('workspace.apps.board.empty.none')
            }
          />
        </Card>
      ) : null}

      {showInstalled && installed.length > 0 ? (
        <Flex vertical gap={10}>
          <Flex align="baseline" gap={8} wrap>
            <SectionLabel>
              {t('workspace.apps.board.installedGroup')}
            </SectionLabel>
            <Text type="secondary" style={{ fontSize: 12 }}>
              {t('workspace.apps.board.installedHint')}
            </Text>
          </Flex>
          <Row gutter={[10, 10]}>
            {installed.map((app) => {
              const launch = buildAppLaunchUrl(app, workspaceDomain)
              const blurb =
                app.description?.trim() ||
                app.instanceTypeDescription?.trim() ||
                ''
              const userCount = countsByAppId.get(Number(app.id))

              return (
                <Col key={app.id} xs={24} sm={12} lg={8} xxl={6}>
                  <Card
                    size="small"
                    variant="outlined"
                    hoverable
                    styles={{ body: { padding: 12 } }}
                    style={{ height: '100%' }}
                    onClick={(e) => {
                      if (isModifiedClick(e)) {
                        return
                      }
                      navigate(detailPath(app))
                    }}
                  >
                    <Flex vertical gap={8} style={{ height: '100%' }}>
                      <Flex align="flex-start" gap={10}>
                        <AppIcon
                          type={app.instanceTypeCode ?? ''}
                          photoUrl={app.iconUrl}
                          settings={app.settings}
                          size={30}
                        />
                        <Flex vertical style={{ flex: 1, minWidth: 0 }}>
                          {/* A real link: keyboard reachable, and cmd-click
                              opens the app detail in a new tab. */}
                          <Link
                            to={detailPath(app)}
                            style={{ color: 'inherit' }}
                          >
                            <Text
                              strong
                              style={{ fontSize: 13.5 }}
                              ellipsis={{ tooltip: app.name }}
                            >
                              {app.name}
                            </Text>
                          </Link>
                          {blurb ? (
                            <Text
                              type="secondary"
                              style={{ fontSize: 11.5 }}
                              ellipsis={{ tooltip: blurb }}
                            >
                              {blurb}
                            </Text>
                          ) : null}
                        </Flex>
                        <Flex
                          gap={4}
                          align="center"
                          onClick={(e) => e.stopPropagation()}
                        >
                          {app.isPublic ? (
                            <Tag
                              style={{
                                marginInlineEnd: 0,
                                fontSize: 10,
                                background: 'var(--ant-color-primary-bg)',
                                color: 'var(--ant-color-primary)',
                                border: 'none'
                              }}
                            >
                              {t('workspace.apps.visibility.public')}
                            </Tag>
                          ) : null}
                          {launch ? (
                            <Button
                              size="small"
                              type="text"
                              icon={<ExternalLink size={14} />}
                              aria-label={t('workspace.apps.board.launch', {
                                name: app.name
                              })}
                              title={t('workspace.apps.board.launch', {
                                name: app.name
                              })}
                              onClick={() =>
                                window.open(
                                  launch,
                                  '_blank',
                                  'noopener,noreferrer'
                                )
                              }
                            />
                          ) : null}
                        </Flex>
                      </Flex>

                      {launch ? (
                        <Text
                          type="secondary"
                          style={{
                            fontSize: 11,
                            fontFamily: 'var(--ant-font-family-code)'
                          }}
                          ellipsis={{ tooltip: launch }}
                        >
                          {launch}
                        </Text>
                      ) : null}

                      <div style={{ flex: 1 }} />

                      <Flex
                        align="center"
                        gap={8}
                        wrap
                        style={{
                          paddingTop: 8,
                          borderTop:
                            '1px solid var(--ant-color-border-secondary)'
                        }}
                      >
                        <Text type="secondary" style={{ fontSize: 11.5 }}>
                          {isCountReliable && userCount !== undefined
                            ? t('workspace.apps.board.userCount', {
                                count: userCount
                              })
                            : t('workspace.apps.board.userCountUnknown')}
                        </Text>
                        <span
                          aria-hidden
                          style={{ color: 'var(--ant-color-split)' }}
                        >
                          ·
                        </span>
                        <Text type="secondary" style={{ fontSize: 11.5 }}>
                          {t('workspace.apps.board.factInstalled')}{' '}
                          <DateTimeCell
                            value={app.createdAt}
                            variant="date"
                            style={{ fontSize: 11.5 }}
                          />
                        </Text>
                      </Flex>
                    </Flex>
                  </Card>
                </Col>
              )
            })}
          </Row>
        </Flex>
      ) : null}

      {showAvailable && available.length > 0 ? (
        <Flex vertical gap={10}>
          <Flex align="baseline" gap={8} wrap>
            <SectionLabel>
              {t('workspace.apps.board.availableGroup')}
            </SectionLabel>
            <Text type="secondary" style={{ fontSize: 12 }}>
              {t('workspace.apps.board.availableHint')}
            </Text>
          </Flex>
          <Row gutter={[10, 10]}>
            {available.map((repo) => (
              <Col key={repo.id} xs={24} sm={12} lg={8} xxl={6}>
                <Card
                  size="small"
                  variant="outlined"
                  styles={{ body: { padding: 12 } }}
                  style={{ height: '100%' }}
                >
                  <Flex vertical gap={8} style={{ height: '100%' }}>
                    <Flex align="flex-start" gap={10}>
                      <AppTypeIcon type={repo.type} size={30} />
                      <Text
                        strong
                        style={{ fontSize: 13.5, flex: 1, minWidth: 0 }}
                        ellipsis={{ tooltip: repo.name }}
                      >
                        {repo.name}
                      </Text>
                    </Flex>

                    <Paragraph
                      type="secondary"
                      style={{ margin: 0, fontSize: 12 }}
                      ellipsis={{
                        rows: 2,
                        tooltip: repo.description ?? undefined
                      }}
                    >
                      {repo.description ||
                        t('workspace.apps.catalogTab.noDescription')}
                    </Paragraph>

                    <div style={{ flex: 1 }} />

                    <div
                      style={{
                        paddingTop: 8,
                        borderTop: '1px solid var(--ant-color-border-secondary)'
                      }}
                    >
                      <Button
                        size="small"
                        type="primary"
                        block
                        icon={<Plus size={14} />}
                        disabled={!canManageApps}
                        onClick={() => setInstallRepo(repo)}
                      >
                        {t('workspace.apps.board.install')}
                      </Button>
                    </div>
                  </Flex>
                </Card>
              </Col>
            ))}
          </Row>
        </Flex>
      ) : null}

      {isFetchingNextPage ? (
        <Flex justify="center" align="center" style={{ minHeight: 40 }}>
          <Spin size="small" />
        </Flex>
      ) : null}

      <InstallRepoDrawer
        workspaceId={workspaceId}
        repo={installRepo}
        open={Boolean(installRepo)}
        onClose={() => setInstallRepo(null)}
      />
    </Flex>
  )
}
